import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@xanectech/ui/components';
import { AlertTriangle } from 'lucide-react';
import {
  createLogger,
  getEnvironmentConfig,
  type EnvironmentConfig,
} from '@xanectech/lib';

const logger = createLogger('Auth - RouteErrorBoundary');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Route-level Error Boundary
 * Catches JavaScript errors anywhere in the route component tree
 * Displays a user-friendly error page instead of crashing the application
 */
class RouteErrorBoundary extends Component<Props, State> {
  private env: EnvironmentConfig;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
    this.env = getEnvironmentConfig();
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const routeContext = {
      route: window.location.pathname,
      errorBoundary: 'RouteErrorBoundary',
    };

    logger.error('Route error boundary caught error', {
      error: error instanceof Error ? error : { error: String(error) },
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
      ...routeContext,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='min-h-screen bg-background flex items-center justify-center p-6'>
          <Card className='w-full max-w-md'>
            <CardHeader className='text-center'>
              <div className='mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center'>
                <AlertTriangle className='h-6 w-6 text-destructive' />
              </div>
              <CardTitle className='text-lg'>Something went wrong</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <p className='text-sm text-muted-foreground text-center'>
                We encountered an unexpected error while loading this page. This
                has been logged and we'll look into it.
              </p>

              {this.env.loggingVars.VITE_DEV && this.state.error && (
                <div className='p-3 bg-muted rounded-md'>
                  <p className='text-xs font-mono text-destructive'>
                    {this.state.error.message}
                  </p>
                </div>
              )}

              <div className='flex flex-col gap-2'>
                <Button onClick={this.handleRetry} className='w-full'>
                  Try Again
                </Button>
                <Button
                  variant='outline'
                  onClick={this.handleGoHome}
                  className='w-full'
                >
                  Go to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
export { RouteErrorBoundary };
